/**
 * CampaignDrawerContext
 *
 * Global context for the campaign drawer that can be triggered from course pages.
 * Follows the same pattern as MessagingContext — manages slide-in drawer state.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { getOrCreateCourseCampaign, listCourseCampaignManifests, materializeCampaignDefinition } from '@/services/courseCampaignService';
import type { CampaignHub, HubTemplate } from '@/services/campaignHubService';
import { getCampaignHubById } from '@/services/campaignHubService';
import type { CampaignManifest } from '@/types/campaignIntegration';

interface CampaignDrawerState {
  isOpen: boolean;
  isLoading: boolean;
  courseId?: string;
  campaignHub?: CampaignHub;
  campaignManifest?: CampaignManifest;
  campaignOptions?: CampaignManifest[];
  error?: string | null;
}

interface CampaignDrawerContextType {
  drawer: CampaignDrawerState;
  openDrawer: (options: {
    courseId: string;
    courseTitle: string;
    template: HubTemplate;
    userId: string;
  }) => void;
  openManifest: (options: {
    courseId: string;
    manifest: CampaignManifest;
  }) => Promise<void>;
  updateCampaignHub: (updates: Partial<CampaignHub>) => void;
  closeDrawer: () => void;
}

const CampaignDrawerContext = createContext<CampaignDrawerContextType | null>(null);

export const useCampaignDrawer = () => {
  const ctx = useContext(CampaignDrawerContext);
  if (!ctx) {
    // Return a no-op version if not wrapped
    return {
      drawer: { isOpen: false, isLoading: false },
      openDrawer: () => {},
      openManifest: async () => {},
      updateCampaignHub: () => {},
      closeDrawer: () => {},
    } as CampaignDrawerContextType;
  }
  return ctx;
};

export const CampaignDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [drawer, setDrawer] = useState<CampaignDrawerState>({
    isOpen: false,
    isLoading: false,
  });

  const openDrawer = useCallback(async (options: {
    courseId: string;
    courseTitle: string;
    template: HubTemplate;
    userId: string;
  }) => {
    // If already open for this course, just toggle open
    if (drawer.isOpen && drawer.courseId === options.courseId && drawer.campaignHub?.template === options.template) {
      return;
    }

    setDrawer({
      isOpen: true,
      isLoading: true,
      courseId: options.courseId,
      error: null,
    });

    try {
      let manifests: CampaignManifest[] = [];
      try { manifests = await listCourseCampaignManifests(options.courseId); } catch { /* legacy courses fall back below */ }
      let manifest = manifests.find((candidate) => candidate.template === options.template) || manifests[0];
      if (manifest && !manifest.materialized && manifest.definitionId) manifest = await materializeCampaignDefinition(manifest.definitionId);
      let hub = manifest?.campaignId ? await getCampaignHubById(manifest.campaignId) : null;
      let error: string | null = null;
      if (!hub && !manifest) {
        const legacy = await getOrCreateCourseCampaign(options.userId, options.courseId, options.courseTitle, options.template);
        hub = legacy.hub;
        error = legacy.error;
      }

      if (error || !hub) {
        setDrawer(prev => ({
          ...prev,
          isLoading: false,
          error: error || 'Failed to load campaign',
          campaignManifest: manifest,
          campaignOptions: manifests,
        }));
        return;
      }

      setDrawer({
        isOpen: true,
        isLoading: false,
        courseId: options.courseId,
        campaignHub: hub,
        campaignManifest: manifest,
        campaignOptions: manifests,
        error: null,
      });
    } catch (err) {
      console.error('CampaignDrawer openDrawer error:', err);
      setDrawer(prev => ({
        ...prev,
        isLoading: false,
        error: 'An unexpected error occurred',
      }));
    }
  }, [drawer.isOpen, drawer.courseId, drawer.campaignHub]);

  const openManifest = useCallback(async ({ courseId, manifest }: {
    courseId: string;
    manifest: CampaignManifest;
  }) => {
    setDrawer({
      isOpen: true,
      isLoading: true,
      courseId,
      campaignManifest: manifest,
      error: null,
    });

    try {
      let selected = manifest;
      if (!selected.materialized && selected.definitionId) {
        selected = await materializeCampaignDefinition(selected.definitionId);
      }
      const hub = selected.campaignId ? await getCampaignHubById(selected.campaignId) : null;
      if (!hub) {
        setDrawer((current) => ({
          ...current,
          isLoading: false,
          campaignManifest: selected,
          error: 'Failed to load campaign',
        }));
        return;
      }
      setDrawer({
        isOpen: true,
        isLoading: false,
        courseId,
        campaignHub: hub,
        campaignManifest: selected,
        campaignOptions: [selected],
        error: null,
      });
    } catch (error) {
      console.error('CampaignDrawer openManifest error:', error);
      setDrawer((current) => ({
        ...current,
        isLoading: false,
        error: 'An unexpected error occurred',
      }));
    }
  }, []);

  const updateCampaignHub = useCallback((updates: Partial<CampaignHub>) => {
    setDrawer(prev => {
      if (!prev.campaignHub) return prev;
      return {
        ...prev,
        campaignHub: {
          ...prev.campaignHub,
          ...updates
        }
      };
    });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawer(prev => ({ ...prev, isOpen: false }));
  }, []);

  return (
    <CampaignDrawerContext.Provider value={{ drawer, openDrawer, openManifest, updateCampaignHub, closeDrawer }}>
      {children}
    </CampaignDrawerContext.Provider>
  );
};
